import { callMBankBridge } from 'shared/lib/mbank-bridge'
import { AdjustEvent, AdjustTokens } from 'shared/services/adjust/constants.ts'

const TIMEOUT_MS = 3000
const DEDUP_PREFIX = 'mtravel_evt:'
const attemptedEvents = new Set<string>()

const getDedupKey = (event: AdjustEvent, dedupId: string) =>
  `${DEDUP_PREFIX}${event}:${dedupId}`

const wasAttempted = (key: string) => {
  if (attemptedEvents.has(key)) return true

  try {
    return localStorage.getItem(key) !== null
  } catch {
    return false
  }
}

const markAttempted = (key: string) => {
  attemptedEvents.add(key)

  try {
    localStorage.setItem(key, String(Date.now()))
  } catch {
    // The in-memory marker still prevents duplicate attempts in this session.
  }
}

const getErrorDetails = (error: unknown) => {
  if (error instanceof Error) {
    return {
      code: 'code' in error ? error.code : undefined,
      message: error.message,
      details: 'details' in error ? error.details : undefined,
    }
  }

  return { details: error }
}

export const trackAdjustEvent = async (
  event: AdjustEvent,
  dedupId: string,
): Promise<void> => {
  if (!dedupId) return

  const dedupKey = getDedupKey(event, dedupId)

  if (wasAttempted(dedupKey)) return

  markAttempted(dedupKey)

  let timeoutId: ReturnType<typeof setTimeout> | undefined

  try {
    const response = await Promise.race([
      callMBankBridge('adjust.logEvent', {
        token: AdjustTokens[event],
        key: 'source',
        value: 'mtravel_webview',
      }),
      new Promise<never>((_, reject) => {
        timeoutId = setTimeout(() => {
          reject(
            Object.assign(new Error('Adjust event timeout'), {
              code: 'timeout',
            }),
          )
        }, TIMEOUT_MS)
      }),
    ])

    if (!response.ok) {
      console.warn('[MTravel][Adjust] event failed', {
        event,
        ...response.error,
      })
    }
  } catch (error) {
    console.warn('[MTravel][Adjust] event failed', {
      event,
      ...getErrorDetails(error),
    })
  } finally {
    if (timeoutId) clearTimeout(timeoutId)
  }
}
